import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
import { isPlanAndBillingIsolationEnabled, isPlanAndBillingPath } from './plan-and-billing-isolation';

const PLAN_AND_BILLING_ZONE = 'PlanAndBilling';
const RECOVERY_STORAGE_KEY = 'cognito.planAndBillingZoneRecovery';

/** Whether the server served this document under the billing zone's policy. */
export function isPlanAndBillingZoneDocument(): boolean {
	return CognitoConfiguration.CspZone === PLAN_AND_BILLING_ZONE;
}

/**
 * Whether the policy this document was served under disagrees with the client's own rule for the route
 * being shown, in either direction. The server picks the policy from the URL the browser asked for, so
 * the two part company when the initial navigation resolves somewhere else -- the boundary guard stands
 * down on the initial load to avoid a reload loop, and both a route's `beforeEnter` redirect and a
 * page-access error redirect run inside it. `settings/selectplan` and `settings/plan/select` redirect into
 * each other and sit on opposite sides of the boundary.
 *
 * Reloading settles that, since the browser then asks for the route it landed on. It cannot settle the two
 * rules genuinely disagreeing about one path, which is why recovery is attempted only once per path.
 */
export function isPlanAndBillingZoneMismatch(path: string): boolean {
	return isPlanAndBillingIsolationEnabled()
		&& isPlanAndBillingPath(path) !== isPlanAndBillingZoneDocument();
}

/** Whether the caller is the one to attempt recovery for a path, recording the attempt so there is only ever one. */
export function tryMarkZoneRecoveryAttempt(path: string): boolean {
	try {
		if (sessionStorage.getItem(RECOVERY_STORAGE_KEY) === path)
			return false;

		sessionStorage.setItem(RECOVERY_STORAGE_KEY, path);
		return true;
	}
	catch {
		return false;
	}
}

/**
 * Whether this document may collect card details. Card entry belongs to the isolated billing document, so
 * it is refused wherever the server did not serve the billing zone's policy. Every route that mounts card
 * fields is inside the zone, so this is a backstop against a routing change putting them outside it. With
 * isolation off there is no separate document to be outside of, so card entry stays permitted wherever it
 * already happens.
 */
export function isCardEntryPermitted(): boolean {
	return !isPlanAndBillingIsolationEnabled() || isPlanAndBillingZoneDocument();
}
